/** @jsx jsx */
import { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import { useRouter } from 'next/router';
import { LoadingDots } from '@balance-web/loading';
import { jsx } from '@balance-web/core';
import { Flex } from '@balance-web/flex';

import { NewPasswordForm, VerifyMFAForm } from '../components/authentication';
import { useAuthenticatedUser, useClient } from '../components/hooks';
import { SignInComponent } from '../components/authentication/signIn';
import { nextAction } from '../components/authentication/useAuthentication';
import { useChallengeUser } from '../components/authentication/useAuthentication/useChallengeUser';
import { Layout } from '../components/layout';
import { useNavigationWithQuery } from '../components/authentication/useNavigationWithQuery';
import { useRedirectToClient } from '../components/authentication/useRedirectToClient';

export default function Page() {
  const router = useRouter();

  const {
    clientId,
    redirectURL,
    tenancy: TENANCY,
    product,
    mfaToken,
  } = useClient();

  const [pageLoading, setPageLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>(''); // TODO: remove when challenge components are built
  const { challengeUser, setChallengeUser } = useChallengeUser(); // TODO: remove when challenge components are built
  const { authenticationStatus, authenticatedUser } = useAuthenticatedUser({
    bypassCache: true,
  });
  const { navigateTo } = useNavigationWithQuery();
  const { redirectToClient, redirectingToClient } = useRedirectToClient();

  const [resetPasswordSuccess, setResetPasswordSuccess] = useState<boolean>(
    false
  );

  useEffect(() => {
    if (authenticatedUser) {
      navigateTo({ path: '' });
    } else if (authenticationStatus === 'unauthenticated') {
      setPageLoading(false);
    }
  }, [authenticatedUser, navigateTo, authenticationStatus]);

  useEffect(() => {
    setResetPasswordSuccess(router?.query?.redirectedFrom === 'reset-password');
  }, [router]);

  const resetPassword = async (value: any) => {
    if (
      !value.password ||
      (challengeUser &&
        challengeUser!.challengeName !== 'NEW_PASSWORD_REQUIRED')
    )
      return;
    try {
      challengeUser!.username =
        challengeUser!.challengeParam.USER_ID_FOR_SRP ||
        challengeUser!.username;
      const resp = await Auth.completeNewPassword(
        challengeUser,
        value.password
      );
      if (
        resp &&
        resp.challengeName &&
        resp.challengeName === 'NEW_PASSWORD_REQUIRED' &&
        resp.signInUserSession &&
        resp.signInUserSession.accessToken
      ) {
        await nextAction({
          user: resp,
          router: router,
          redirectURL,
          TENANCY,
          clientId,
          mfaToken,
        });
      } else {
        setChallengeUser(null);
      }
    } catch (err) {
      setErrorMessage(err.message);
      if (err.message.includes('session is expired')) {
        router.push({ pathname: '/login', query: router.query });
      }
    }
  };

  const confirmMFA = async (value: any) => {
    if (
      !value.code ||
      (challengeUser && challengeUser.challengeName !== 'SMS_MFA')
    )
      return;
    try {
      challengeUser!.username =
        challengeUser!.challengeParam.USER_ID_FOR_SRP ||
        challengeUser!.username;
      const resp = await Auth.confirmSignIn(
        challengeUser,
        value.code,
        'SMS_MFA',
        {
          MFAValidation: '1',
        }
      );
      if (
        resp &&
        resp.challengeName &&
        resp.challengeName === 'SMS_MFA' &&
        resp.signInUserSession &&
        resp.signInUserSession.accessToken
      ) {
        await nextAction({
          user: resp,
          router: router,
          redirectURL,
          TENANCY,
          clientId,
          mfaToken,
        });
      }
    } catch (err) {
      setErrorMessage(err.message);
      if (err.message.includes('session is expired')) {
        router.push({ pathname: '/login', query: router.query });
      }
    }
  };

  if (!clientId || pageLoading || redirectingToClient) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh">
        <LoadingDots label="Intial data loading" size="large" />
      </Flex>
    );
  }

  return (
    <Layout theme={TENANCY} product={product}>
      {challengeUser && challengeUser.challengeName === 'SMS_MFA' ? (
        <VerifyMFAForm
          onSubmit={confirmMFA}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
        />
      ) : challengeUser &&
        challengeUser.challengeName === 'NEW_PASSWORD_REQUIRED' ? (
        <NewPasswordForm
          onSubmit={resetPassword}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
        />
      ) : (
        <SignInComponent
          resetPasswordSuccess={resetPasswordSuccess}
          resetResetPasswordSuccess={() => setResetPasswordSuccess(false)}
          onRedirectToClient={() => redirectToClient()}
          navigateToResetPassword={() => navigateTo({ path: 'reset-password' })}
          navigateToSignup={() => navigateTo({ path: 'signup' })}
          navigateToForgotPassword={() =>
            navigateTo({ path: 'forgot-password' })
          }
          navigateToVerifyMfa={() => navigateTo({ path: 'verifycode' })}
          navigateToSetupMfa={() => navigateTo({ path: 'mfa' })}
        />
      )}
    </Layout>
  );
}
